import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { tokens } from "../../../theme";
import apiService from "../../../service/Api";
import ContactDetailsDialog from "../../bookings/view-booking2/tabs/booking-details/components/ContactDetailsDialog";
import WhatsAppDialog from "../../bookings/view-booking2/tabs/booking-details/components/WhatsAppDialog";
import type { OrganizerCardProps } from "../../bookings/view-booking2/types/types";

interface OrganiserPanelProps {
  organiser: {
    leadName: string;
    orgName: string;
    email: string;
    phoneNumber: string;
    experience: string;
  };
  contactId?: number | null;
  appInfo: {
    id: string;
    submitted: string;
  };
  eventFlyer: string | null;
  onFlyerUpload: (file: File) => Promise<void>;
}

interface RawContact {
  id: number;
  contact_type: "individual" | "company";
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  contact_image?: string | null;
  address_one?: string | null;
  address_two?: string | null;
  role?: string | null;
  notes?: string | null;
}

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

function getFlyerFilename(url: string): string {
  return url.split("?")[0].split("/").pop() ?? "flyer";
}

export default function OrganiserPanel({
  organiser,
  contactId,
  appInfo,
  eventFlyer,
  onFlyerUpload,
}: OrganiserPanelProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const cardBg = colors.primary[900];
  const mutedCard = colors.primary[800];
  const textPrimary = colors.primary[700];
  const textMuted = colors.primary[300];

  const [notes, setNotes] = useState("");
  const [editNotes, setEditNotes] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [contactClient, setContactClient] = useState<OrganizerCardProps | null>(null);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isWhatsAppDialogOpen, setIsWhatsAppDialogOpen] = useState(false);

  useEffect(() => {
    if (!contactId) {
      setContactClient(null);
      return;
    }
    let cancelled = false;
    apiService
      .get<RawContact>(`/api/contacts/contact/${contactId}/`)
      .then((res) => {
        if (cancelled) return;
        const c = res.data;
        setContactClient({
          id: c.id,
          contact_type: c.contact_type,
          name:
            `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
            c.name ||
            organiser.leadName,
          relation: c.role || organiser.orgName || "N/A",
          email: c.email || organiser.email,
          phone: c.phone || organiser.phoneNumber,
          address:
            [c.address_one, c.address_two].filter(Boolean).join(", ") ||
            "N/A",
          contact_image: c.contact_image || undefined,
          specialNotes: "N/A",
          notes: c.notes || "N/A",
        });
      })
      .catch(() => {
        if (!cancelled) setContactClient(null);
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, organiser.leadName, organiser.orgName, organiser.email, organiser.phoneNumber]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      await onFlyerUpload(file);
    } catch {
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDownload = () => {
    if (!eventFlyer) return;
    const a = document.createElement("a");
    a.href = eventFlyer;
    a.download = getFlyerFilename(eventFlyer);
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
  };

  return (
    <>
    <Stack spacing={2.5} >
      {/* Profile & Program Card */}
      <Card
        elevation={0}
        sx={{ bgcolor: cardBg, borderRadius: "18px", overflow: "hidden" }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2.5,
              cursor: contactClient ? "pointer" : "default",
              "&:hover": contactClient ? { opacity: 0.85 } : undefined,
            }}
            onClick={() => contactClient && setIsContactDialogOpen(true)}
          >
            <Avatar
              src={contactClient?.contact_image}
              sx={{
                width: 52,
                height: 52,
                bgcolor: "#0F4D92",
                fontSize: 17,
                fontWeight: 700,
              }}
            >
              {!contactClient?.contact_image && getInitials(organiser.leadName)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: textPrimary,
                  lineHeight: 1.2,
                }}
              >
                {organiser.leadName}
              </Typography>
              <Typography
                sx={{
                  mt: 0.3,
                  fontSize: 13,
                  color: textMuted,
                  lineHeight: 1.2,
                }}
              >
                {organiser.orgName}
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={0.75}
              onClick={(e) => e.stopPropagation()}
            >
              <IconButton
                size="small"
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: "#FFF1F222",
                  color: "#EA5B71",
                  border: "1px solid #EA5B7122",
                  "&:hover": { bgcolor: "#FFF1F244" },
                }}
              >
                <EmailOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
              <IconButton
                size="small"
                component="a"
                href={`https://wa.me/${organiser.phoneNumber}`}
                target="_blank"
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: "#EEFCEF22",
                  color: "#35B66B",
                  border: "1px solid #35B66B22",
                  "&:hover": { bgcolor: "#EEFCEF44" },
                }}
              >
                <WhatsAppIcon sx={{ fontSize: 17 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  navigator.clipboard.writeText(organiser.phoneNumber)
                }
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: mutedCard,
                  color: textMuted,
                  border: `1px solid ${textMuted}22`,
                  "&:hover": { bgcolor: colors.primary[800] },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Stack>
          </Box>

          <Box
            sx={{ bgcolor: mutedCard, borderRadius: "12px", p: 1.75, mb: 2 }}
          >
            {[
              { label: "Email", value: organiser.email },
              { label: "Phone", value: organiser.phoneNumber },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 0.75,
                }}
              >
                <Typography
                  sx={{ fontSize: 12, color: textMuted, fontWeight: 500 }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: textPrimary, fontWeight: 600 }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              bgcolor: mutedCard,
              borderRadius: "14px",
              p: 1.75,
              mb: 2,
              border: `1px solid ${textMuted}14`,
            }}
          >
            <Box
              sx={{
                mb: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: textPrimary }}
              >
                Notes
              </Typography>
              <IconButton
                size="small"
                onClick={() => setEditNotes(!editNotes)}
                sx={{ width: 28, height: 28, color: colors.primary[400] }}
              >
                {editNotes ? (
                  <CheckIcon sx={{ fontSize: 17 }} />
                ) : (
                  <EditOutlinedIcon sx={{ fontSize: 17 }} />
                )}
              </IconButton>
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!editNotes}
              placeholder="Add notes..."
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  p: 0,
                  bgcolor: "transparent",
                  borderRadius: 2,
                  alignItems: "flex-start",
                  "& fieldset": {
                    borderWidth: editNotes ? 1 : 0,
                    borderColor: `${textMuted}30`,
                  },
                },
                "& textarea": {
                  padding: editNotes ? "10px 12px" : "0px",
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: textPrimary,
                },
              }}
            />
          </Box>

          <Box
            sx={{
              bgcolor: mutedCard,
              borderRadius: "14px",
              p: 1.75,
              border: `1px solid ${textMuted}14`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: eventFlyer ? 1.5 : 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 700, color: textPrimary }}
                >
                  Program
                </Typography>
                {!eventFlyer && (
                  <Chip
                    label="None"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 10,
                      bgcolor: `${textMuted}18`,
                      color: textMuted,
                      borderRadius: "6px",
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                )}
              </Box>

              <Stack direction="row" spacing={0.5}>
                {eventFlyer && (
                  <Tooltip title="Download" placement="top">
                    <IconButton
                      size="small"
                      onClick={handleDownload}
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: "#3B82F614",
                        color: "#3B82F6",
                        border: "1px solid #3B82F622",
                        "&:hover": { bgcolor: "#3B82F624" },
                      }}
                    >
                      <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={eventFlyer ? "Replace image" : "Upload event flyer"}
                  placement="top"
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: uploading ? `${textMuted}10` : "#4BA66314",
                        color: uploading ? textMuted : "#4BA663",
                        border: `1px solid ${uploading ? `${textMuted}18` : "#4BA66322"}`,
                        "&:hover": { bgcolor: "#4BA66324" },
                      }}
                    >
                      {uploading ? (
                        <CircularProgress size={12} sx={{ color: textMuted }} />
                      ) : (
                        <FileUploadOutlinedIcon sx={{ fontSize: 15 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {uploadError && (
              <Typography
                sx={{ fontSize: 11, color: "#EB4335", mb: 1, mt: 0.5 }}
              >
                {uploadError}
              </Typography>
            )}

            {!eventFlyer ? (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  mt: 1.25,
                  borderRadius: "10px",
                  border: `1.5px dashed ${textMuted}30`,
                  py: 2.5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "pointer",
                  "&:hover": { borderColor: `${textMuted}60` },
                }}
              >
                <FileUploadOutlinedIcon
                  sx={{ fontSize: 22, color: textMuted }}
                />
                <Typography
                  sx={{ fontSize: 12, color: textMuted, fontWeight: 500 }}
                >
                  Upload event flyer
                </Typography>
                <Typography sx={{ fontSize: 11, color: textMuted }}>
                  JPG, PNG, WebP, PDF
                </Typography>
              </Box>
            ) : eventFlyer.toLowerCase().includes(".pdf") ? (
              <Box
                component="a"
                href={eventFlyer}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.75,
                  borderRadius: "10px",
                  bgcolor: `${textMuted}08`,
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { bgcolor: `${textMuted}14` },
                }}
              >
                <FileDownloadOutlinedIcon
                  sx={{ fontSize: 28, color: "#EB4335" }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: textPrimary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getFlyerFilename(eventFlyer)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#3B82F6" }}>
                    Click to open
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: `1px solid ${textMuted}18`,
                }}
              >
                <img
                  src={eventFlyer}
                  alt="Event flyer"
                  style={{
                    width: "100%",
                    display: "block",
                    objectFit: "cover",
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Card>

      {/* Application Info Card */}
      <Card elevation={0} sx={{ bgcolor: cardBg, borderRadius: "18px" }}>
        <Box sx={{ p: 2.5 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: textMuted,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              mb: 1.5,
            }}
          >
            Application Info
          </Typography>
          {[
            { label: "Confirmation ID", value: appInfo.id },
            {
              label: "Date Submitted",
              value: new Date(appInfo.submitted).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              }),
            },
          ].map((item) => (
            <Box
              key={item.label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 0.75,
              }}
            >
              <Typography sx={{ fontSize: 12, color: textMuted }}>
                {item.label}
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: textPrimary, fontWeight: 600 }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>

      <Card
        elevation={0}
        sx={{
          bgcolor: cardBg,
          borderRadius: "18px",
          maxHeight: 320,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            p: 2.5,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Typography
            sx={{ fontSize: 14, fontWeight: 700, color: textPrimary, mb: 1.5 }}
          >
            Organiser Experience
          </Typography>
          <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, minHeight: 0 }}>
            <Typography
              sx={{ fontSize: 14, color: textMuted, lineHeight: 1.7 }}
            >
              {organiser.experience}
            </Typography>
          </Box>
        </Box>
      </Card>
    </Stack>

    <ContactDetailsDialog
      open={isContactDialogOpen}
      client={contactClient ?? undefined}
      currentUserName="Muzukuru Staff"
      onClose={() => setIsContactDialogOpen(false)}
      onWhatsAppClick={() => {
        setIsContactDialogOpen(false);
        setIsWhatsAppDialogOpen(true);
      }}
      onBookingClick={(bookingId) => {
        setIsContactDialogOpen(false);
        navigate(`/bookings/${bookingId}`);
      }}
    />

    <WhatsAppDialog
      open={isWhatsAppDialogOpen}
      onClose={() => setIsWhatsAppDialogOpen(false)}
      client={contactClient ?? undefined}
      currentUserName="Muzukuru Staff"
    />
    </>
  );
}
